<script setup lang="ts">
import { useShare, useVModel } from '@vueuse/core';
import type { ReportReason } from '@/gql/graphql';
import {
  ReportCreateModal,
  type ReportCreateModalResult,
} from '@/features/reports';
import type { PartnerType } from '../types';
import { useQuery } from '@urql/vue';
import { graphql } from '@/gql';
import type { RouteLocationRaw } from 'vue-router';

export type PartnerContextMenuReportData = {
  reason: ReportReason;
  text: string;
};

const props = defineProps<{
  modelValue?: boolean;
  partnerType: PartnerType;
  partnerId: number;
  blockRedirectLink?: RouteLocationRaw;
}>();
defineEmits<{
  (e: 'update:model-value', modelValue: boolean): void;
}>();

const modelValue = useVModel(props);
const isReportModalVisible = ref(false);
const { share } = useShare();
const { showReportedToast, showBlockedToast } = useContextToasts();

const { data: masterData } = useQuery({
  query: graphql(/* GraphQL */ `
    query PartnerContextMenu_MasterQuery($id: Int!) {
      master(id: $id) {
        id
        fullName
      }
    }
  `),
  variables: toRef(() => ({ id: props.partnerId })),
  pause: toRef(() => props.partnerType !== 'master'),
});
const { data: companyData } = useQuery({
  query: graphql(/* GraphQL */ `
    query PartnerContextMenu_CompanyQuery($id: Int!) {
      company(id: $id) {
        id
        name
      }
    }
  `),
  variables: toRef(() => ({ id: props.partnerId })),
  pause: toRef(() => props.partnerType !== 'company'),
});
const { executeMutation: reportMaster } = useMutation(
  graphql(/* GraphQL */ `
    mutation PartnerContextMenu_ReportMasterMutation(
      $id: Int!
      $input: ReportInput!
    ) {
      reportMaster(id: $id, input: $input) {
        id
      }
    }
  `)
);
const { executeMutation: reportCompany } = useMutation(
  graphql(/* GraphQL */ `
    mutation PartnerContextMenu_ReportCompanyMutation(
      $id: Int!
      $input: ReportInput!
    ) {
      reportCompany(id: $id, input: $input) {
        id
      }
    }
  `)
);
const { executeMutation: blockMaster } = useMutation(
  graphql(/* GraphQL */ `
    mutation PartnerContextMenu_BlockUserMutation($id: Int!) {
      blockUser(id: $id)
    }
  `)
);
const { executeMutation: blockCompany } = useMutation(
  graphql(/* GraphQL */ `
    mutation PartnerContextMenu_BlockCompanyMutation($id: Int!) {
      blockCompany(id: $id)
    }
  `)
);

function hide() {
  modelValue.value = false;
}
function onShare() {
  const title =
    props.partnerType === 'master'
      ? masterData.value?.master.fullName
      : companyData.value?.company.name;
  if (!title) return;
  share({ title, url: location.href });
  hide();
}
async function onReport(input: ReportCreateModalResult) {
  const report = props.partnerType === 'master' ? reportMaster : reportCompany;
  await report({ id: props.partnerId, input });
  showReportedToast();

  isReportModalVisible.value = false;
  modelValue.value = false;
}
async function onBlock() {
  const block = props.partnerType === 'master' ? blockMaster : blockCompany;
  await block({ id: props.partnerId });
  showBlockedToast();

  modelValue.value = false;
  if (props.blockRedirectLink) navigateTo(props.blockRedirectLink);
}
</script>

<template>
  <AppModal v-model="modelValue" width="xs">
    <AppGroup class="mb-3">
      <AppButton variant="ghost" fluid @click="onShare">Поделиться</AppButton>
      <AppButton
        variant="ghost"
        color="danger"
        fluid
        @click="isReportModalVisible = true"
      >
        Пожаловаться
      </AppButton>
      <AppButton variant="ghost" color="danger" fluid @click="onBlock">
        Заблокировать
      </AppButton>
    </AppGroup>
    <AppGroup>
      <AppButton variant="ghost" fluid @click="hide">Отмена</AppButton>
    </AppGroup>
    <ReportCreateModal v-model="isReportModalVisible" @done="onReport" />
  </AppModal>
</template>
